import { useContext, useMemo, useState } from "react";
import { useRestaurantAccess } from "../../../access/hooks/useRestaurantAccess";
import { Context } from "../../../context/Context";
import { Button, Input, Pencil, Select } from "../../ui";
import { editCampaignAPI } from "../../../utils/api/actions";
import { MAX_CAMPAIGN_NAME, campaignTypeLabel, getCampaignTypeOptions } from "../operationUtils";
import { getCampaignText } from "./campaignActionsText";
import CampaignModal from "./CampaignModal";
import { describeError } from "./useStoreCampaigns";

const toInputDate = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");

// Edits what the API allows after submission: name, details, dates, and the
// type while the request is still pending. The lifecycle state never changes.
export default function EditCampaignModal({ action, stores, onClose, onSaved }) {
  // `stores`: an already-loaded stores list to resolve the restaurant against (optional)
  const { canFor } = useRestaurantAccess(stores);
  const { lang } = useContext(Context);
  const text = getCampaignText(lang);
  const firstLine = String(action.content || "").split("\n")[0].trim();
  const [name, setName] = useState(action.campaignName || firstLine);
  const [details, setDetails] = useState(action.campaignDetails || "");
  const [campaignType, setCampaignType] = useState(action.campaignType || "");
  const [startDate, setStartDate] = useState(toInputDate(action.startDate));
  const [endDate, setEndDate] = useState(toInputDate(action.endDate));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // edit -> confirm: nothing is saved before the second step
  const [confirming, setConfirming] = useState(false);

  const typeEditable = action.status === "pending" && action.campaignType !== "promoCamp";
  const typeOptions = useMemo(() => getCampaignTypeOptions(lang), [lang]);
  const datesInvalid = Boolean(startDate && endDate && endDate < startDate);
  const needsDetails = action.source !== "catalog";
  // Only a changed name is held to the limit (a long catalog name stays as is).
  const originalName = action.campaignName || firstLine;
  const nameTooLong = name.trim() !== originalName && name.trim().length > MAX_CAMPAIGN_NAME;
  const invalid = nameTooLong || !name.trim() || (needsDetails && !details.trim()) || datesInvalid;

  const handleSave = async () => {
    // handler-level authorization, against the campaign's restaurant
    if (!canFor("operation.campaign.edit", action.storeId)) return;
    if (invalid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const changes = {
        campaignName: name.trim(),
        campaignDetails: details.trim(),
        startDate,
        endDate,
        ...(typeEditable && campaignType !== action.campaignType ? { campaignType } : {}),
      };
      const saved = await editCampaignAPI(localStorage.getItem("auth-token"), action._id, changes);
      onSaved(saved);
      onClose();
    } catch (failure) {
      setError(describeError(failure, text));
      setSubmitting(false);
    }
  };

  return (
    <CampaignModal
      title={text.editCampaign}
      icon={<Pencil size={20} aria-hidden="true" />}
      onClose={onClose}
      busy={submitting}
      closeLabel={text.close}
      footer={
        <>
          {confirming ? (
            <>
              <Button variant="primary" appearance="filled" disabled={invalid || submitting} loading={submitting} onClick={handleSave}>
                {submitting ? text.saving : text.confirmUpdate}
              </Button>
              <Button variant="neutral" appearance="soft" disabled={submitting} onClick={() => setConfirming(false)}>
                {text.back}
              </Button>
            </>
          ) : (
            <>
              <Button variant="primary" appearance="filled" disabled={invalid} onClick={() => setConfirming(true)}>
                {text.save}
              </Button>
              <Button variant="neutral" appearance="soft" onClick={onClose}>
                {text.close}
              </Button>
            </>
          )}
        </>
      }
    >
      {confirming && <p className="ca-hint" role="status">{text.confirmUpdateText}</p>}
      <fieldset className="ca-fieldset" disabled={confirming}>
      <Input
        label={text.campaignName}
        required
        value={name}
        error={nameTooLong ? text.nameTooLong(MAX_CAMPAIGN_NAME, name.trim().length) : undefined}
        onChange={(event) => setName(event.target.value)}
      />
      {typeEditable ? (
        <Select label={text.campaignType} options={typeOptions} value={campaignType} onChange={(event) => setCampaignType(event.target.value)} />
      ) : (
        <Input label={text.campaignType} value={campaignTypeLabel(action.campaignType, lang)} readOnly helperText={text.editTypeLocked} />
      )}
      <Input textarea required={needsDetails} label={text.campaignDetails} value={details} maxLength={4000} onChange={(event) => setDetails(event.target.value)} />
      <div className="ca-grid-2">
        <Input type="date" label={text.startDate} value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        <Input type="date" label={text.endDate} value={endDate} min={startDate || undefined} error={datesInvalid || undefined} onChange={(event) => setEndDate(event.target.value)} />
      </div>
      <p className="ca-hint">{text.datesHint}</p>
      </fieldset>
      {error && (
        <p className="ca-error" role="alert">
          {error}
        </p>
      )}
    </CampaignModal>
  );
}
